import type {
	API_CreateEstablishmentRequest,
	API_CreateEstablishmentResponse,
	API_ListOrganizationEstablishmentsPaginatedResponse,
	API_ListOrganizationEstablishmentsResponse,
	API_UpdateEstablishmentRequest,
	API_UpdateEstablishmentResponse,
	Establishment,
	PaginationRequest,
} from "@pilatestime/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// API functions
export const fetchOrganizationEstablishments = (
	token: string,
	organizationId: string,
): Promise<API_ListOrganizationEstablishmentsResponse> =>
	api
		.get(`/organizations/${organizationId}/establishments`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data.data); // Extract data from paginated response

export const fetchOrganizationEstablishmentsPaginated = (
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
): Promise<API_ListOrganizationEstablishmentsPaginatedResponse> =>
	api
		.get(`/organizations/${organizationId}/establishments`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				...paginationParams,
				...(searchTerm && { search: searchTerm }),
			},
		})
		.then((res) => res.data);

export const fetchEstablishmentById = (
	token: string,
	organizationId: string,
	establishmentId: string,
): Promise<Establishment> =>
	api
		.get(`/organizations/${organizationId}/establishments/${establishmentId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const createEstablishment = (
	token: string,
	organizationId: string,
	data: API_CreateEstablishmentRequest,
): Promise<API_CreateEstablishmentResponse> =>
	api
		.post(`/organizations/${organizationId}/establishments`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const updateEstablishment = (
	token: string,
	organizationId: string,
	establishmentId: string,
	data: API_UpdateEstablishmentRequest,
): Promise<API_UpdateEstablishmentResponse> =>
	api
		.put(
			`/organizations/${organizationId}/establishments/${establishmentId}`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const deleteEstablishment = (
	token: string,
	organizationId: string,
	establishmentId: string,
): Promise<void> =>
	api
		.delete(
			`/organizations/${organizationId}/establishments/${establishmentId}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then(() => undefined);

// Query options
export const fetchOrganizationEstablishmentsQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["organization_establishments", organizationId, userId],
		queryFn: () => fetchOrganizationEstablishments(token, organizationId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchOrganizationEstablishmentsPaginatedQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
) =>
	queryOptions({
		queryKey: searchTerm
			? [
					"organization_establishments_paginated_search",
					organizationId,
					userId,
					paginationParams,
					searchTerm,
				]
			: [
					"organization_establishments_paginated",
					organizationId,
					userId,
					paginationParams,
				],
		queryFn: () =>
			fetchOrganizationEstablishmentsPaginated(
				token,
				organizationId,
				paginationParams,
				searchTerm,
			),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchEstablishmentByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	establishmentId: string,
) =>
	queryOptions({
		queryKey: ["establishment", organizationId, establishmentId, userId],
		queryFn: () =>
			fetchEstablishmentById(token, organizationId, establishmentId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// Mutation hooks
export const useCreateEstablishment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_CreateEstablishmentRequest;
		}) => createEstablishment(token, organizationId, data),
		toastMessages: {
			loading: t("common.toasts.establishmentCreating"),
			success: t("common.toasts.establishmentCreated"),
			error: t("common.toasts.establishmentCreateError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization establishments (both paginated and non-paginated)
			queryClient.invalidateQueries({
				queryKey: ["organization_establishments", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_establishments_paginated",
					variables.organizationId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_establishments_paginated_search",
					variables.organizationId,
				],
			});
			// Invalidate schedule sessions that might be affected by new establishment
			queryClient.invalidateQueries({
				queryKey: ["schedule_sessions"],
			});
		},
	});
};

export const useUpdateEstablishment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			establishmentId,
			data,
		}: {
			token: string;
			organizationId: string;
			establishmentId: string;
			data: API_UpdateEstablishmentRequest;
		}) => updateEstablishment(token, organizationId, establishmentId, data),
		toastMessages: {
			loading: t("common.toasts.establishmentUpdating"),
			success: t("common.toasts.establishmentUpdated"),
			error: t("common.toasts.establishmentUpdateError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization establishments (both paginated and non-paginated)
			queryClient.invalidateQueries({
				queryKey: ["organization_establishments", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_establishments_paginated",
					variables.organizationId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_establishments_paginated_search",
					variables.organizationId,
				],
			});

			// Invalidate specific establishment
			queryClient.invalidateQueries({
				queryKey: [
					"establishment",
					variables.organizationId,
					variables.establishmentId,
				],
			});
			// Invalidate schedule sessions that might be affected by establishment changes
			queryClient.invalidateQueries({
				queryKey: ["schedule_sessions"],
			});
		},
	});
};

export const useDeleteEstablishment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			establishmentId,
		}: {
			token: string;
			organizationId: string;
			establishmentId: string;
		}) => deleteEstablishment(token, organizationId, establishmentId),
		toastMessages: {
			loading: t("common.toasts.establishmentDeleting"),
			success: t("common.toasts.establishmentDeleted"),
			error: t("common.toasts.establishmentDeleteError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization establishments (both paginated and non-paginated)
			queryClient.invalidateQueries({
				queryKey: ["organization_establishments", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_establishments_paginated",
					variables.organizationId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_establishments_paginated_search",
					variables.organizationId,
				],
			});

			// Remove specific establishment from cache
			queryClient.removeQueries({
				queryKey: [
					"establishment",
					variables.organizationId,
					variables.establishmentId,
				],
			});
			// Invalidate schedule sessions that might be affected by establishment deletion
			queryClient.invalidateQueries({
				queryKey: ["schedule_sessions"],
			});
			// Invalidate schedules that might be affected by establishment deletion
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
		},
	});
};
